import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ReportSelectionOption } from '~/shared/utils/types';
import { refreshStateMenuItems } from '~/shared/components/refreshStateMenuItems';

import { getApiFilterFromSelectOption } from '../../common/helpers';
import { CUSTOM_DATES_TITLE } from '../../common/dateFilterMenuItems';
import { getTableDateRange, parseSortField } from '../helpers';

const OPTIMIZATION_TABLE_SORT_KEY = 'sort_field';

interface UseOptimizationTableParamsArgs {
  activeDateFilter: string | null;
  apiFilters: string | null;
  dataLimit: number;
  pageOffset: string;
  activeRefreshState?: string | null;
}

export function useOptimizationTableParams({
  activeDateFilter,
  apiFilters,
  dataLimit,
  pageOffset,
  activeRefreshState,
}: UseOptimizationTableParamsArgs) {
  const [searchParams] = useSearchParams();
  const [currentTime, setCurrentTime] = useState(new Date());

  const sortField = useMemo(
    () => parseSortField(searchParams.get(OPTIMIZATION_TABLE_SORT_KEY)),
    [searchParams]
  );

  const currentReportFilters = useMemo(
    () => JSON.parse(apiFilters || '[]') as ReportSelectionOption[],
    [apiFilters]
  );

  // Set up time-based polling for relative date filters
  useEffect(() => {
    if (!activeRefreshState || activeRefreshState === 'Off') {
      return;
    }

    const isRelativeFilter = !activeDateFilter?.includes(CUSTOM_DATES_TITLE);
    if (!isRelativeFilter) {
      return;
    }

    const refreshItem = refreshStateMenuItems.find(item => item.title === activeRefreshState);
    const interval = refreshItem?.time || 0;

    if (interval > 0) {
      const intervalId = setInterval(() => {
        setCurrentTime(new Date());
      }, interval);

      return () => clearInterval(intervalId);
    }
  }, [activeRefreshState, activeDateFilter]);

  const [tableStartDate, tableEndDate] = useMemo(
    () => getTableDateRange(activeDateFilter),
    [activeDateFilter, currentTime]
  );

  const parsedFilters = useMemo(() => {
    const processedFilters = searchParams.get('processed_filters');
    if (processedFilters) {
      return JSON.parse(processedFilters) as string[];
    }
    const result = getApiFilterFromSelectOption(currentReportFilters);
    return Array.isArray(result) ? result : [result];
  }, [searchParams, currentReportFilters]);

  const optimizationTableBaseParams: [string, string][] = useMemo(
    () => [
      ['start_date', tableStartDate],
      ['end_date', tableEndDate],
      ...parsedFilters.map((f) => ['filters', f] as [string, string]),
    ],
    [tableStartDate, tableEndDate, parsedFilters]
  );

  const optimizationTableQueryString = useMemo(
    () =>
      new URLSearchParams([
        ...optimizationTableBaseParams,
        [
          'sortBy',
          JSON.stringify({
            field: sortField.field,
            ascending: sortField.dir === 'asc',
          }),
        ],
        ['limit', dataLimit.toString()],
        ['offset', pageOffset],
      ]).toString(),
    [optimizationTableBaseParams, sortField, pageOffset, dataLimit]
  );

  const optimizationTableCountQueryString = useMemo(
    () => new URLSearchParams(optimizationTableBaseParams).toString(),
    [optimizationTableBaseParams]
  );

  const filtersSignature = useMemo(
    () => (parsedFilters.length ? parsedFilters.slice().sort().join('|') : 'none'),
    [parsedFilters]
  );

  const timeSignature = useMemo(
    () => `${tableStartDate}|${tableEndDate}`,
    [tableStartDate, tableEndDate]
  );

  return {
    sortField,
    currentReportFilters,
    parsedFilters,
    optimizationTableQueryString,
    optimizationTableCountQueryString,
    filtersSignature,
    timeSignature,
  };
}
